import React, { useMemo } from 'react';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';

export default function PlanWizardShell({
  steps = [],
  currentStep = 0,
  onBack,
  onNext,
  onSubmit = null,
  submitLabel = 'Submit for Approval',
  submitDisabled = false,
  onSaveDraft = null,
  backDisabled = false,
  nextDisabled = false,
  nextLabel = 'Next',
  progress = null,
  children,
  planName = null,
  planCode = null,
  planStatus = null,
}) {
  const safeCurrentStep = useMemo(() => {
    if (steps.length === 0) return 0;
    return Math.min(Math.max(currentStep, 0), steps.length - 1);
  }, [currentStep, steps.length]);

  const isLastStep = steps.length > 0 && safeCurrentStep === steps.length - 1;

  const progressState = useMemo(() => {
    if (!progress) return null;

    const configured = Number.isFinite(progress.configured) ? progress.configured : 0;
    const total = Number.isFinite(progress.total) ? progress.total : 0;
    const boundedConfigured = Math.max(0, configured);
    const boundedTotal = Math.max(0, total);
    const isComplete = boundedTotal > 0 && boundedConfigured >= boundedTotal;
    const percentage = boundedTotal > 0
      ? Math.min((boundedConfigured / boundedTotal) * 100, 100)
      : 0;

    return {
      configured: boundedConfigured,
      total: boundedTotal,
      label: progress.label || '',
      isComplete,
      percentage,
    };
  }, [progress]);

  const statusConfig = useMemo(() => {
    const normalizedStatus = typeof planStatus === 'string'
      ? planStatus.toLowerCase()
      : null;

    const configs = {
      draft: {
        label: 'Draft',
        className:
          'bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))] border-[rgb(var(--color-warning))]/30',
      },
      pending_approval: {
        label: 'Pending approval',
        className:
          'bg-[rgb(var(--color-info))]/10 text-[rgb(var(--color-info))] border-[rgb(var(--color-info))]/30',
      },
      approved: {
        label: 'Approved',
        className:
          'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))] border-[rgb(var(--color-success))]/30',
      },
    };

    return normalizedStatus ? configs[normalizedStatus] || null : null;
  }, [planStatus]);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-[rgb(var(--color-bg))] text-[rgb(var(--color-text))]">
      {planName ? (
        <div className="flex min-h-10 shrink-0 items-center gap-2 border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-6 py-2">
          <span className="truncate text-tok-sm font-semibold text-[rgb(var(--color-text))]">
            {planName}
          </span>

          {planCode ? (
            <>
              <span
                aria-hidden="true"
                className="text-tok-xs text-[rgb(var(--color-text-muted))]"
              >
                ·
              </span>
              <span className="truncate font-mono text-tok-xs text-[rgb(var(--color-text-muted))]">
                {planCode}
              </span>
            </>
          ) : null}

          {statusConfig ? (
            <>
              <span
                aria-hidden="true"
                className="text-tok-xs text-[rgb(var(--color-text-muted))]"
              >
                ·
              </span>
              <span
                className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-tok-xs font-medium ${statusConfig.className}`}
              >
                {statusConfig.label}
              </span>
            </>
          ) : null}
        </div>
      ) : null}

      {steps.length > 0 ? (
        <nav
          aria-label="Plan creation progress"
          className="shrink-0 overflow-x-auto border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-6 py-3"
        >
          <ol className="mx-auto flex min-w-max items-start justify-center">
            {steps.map((step, index) => {
              const isCompleted = index < safeCurrentStep;
              const isActive = index === safeCurrentStep;

              return (
                <React.Fragment key={step.key}>
                  <li className="flex min-w-20 flex-col items-center text-center">
                    <div
                      aria-current={isActive ? 'step' : undefined}
                      className="flex flex-col items-center"
                    >
                      <span
                        className={[
                          'flex items-center justify-center rounded-full border-2 transition-all',
                          isActive ? 'h-7 w-7' : 'h-6 w-6',
                          isCompleted
                            ? 'border-[rgb(var(--color-success))] bg-[rgb(var(--color-success))] text-white'
                            : '',
                          isActive
                            ? 'border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))] text-white'
                            : '',
                          !isCompleted && !isActive
                            ? 'border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text-muted))]'
                            : '',
                        ].join(' ')}
                      >
                        {isCompleted ? <Check aria-hidden="true" className="h-3.5 w-3.5" /> : null}
                      </span>

                      <span
                        className={[
                          'mt-1.5 whitespace-nowrap text-tok-xs font-medium',
                          isActive
                            ? 'text-[rgb(var(--color-primary))]'
                            : 'text-[rgb(var(--color-text-muted))]',
                          isActive ? 'block' : 'hidden sm:block',
                        ].join(' ')}
                      >
                        {step.label}
                      </span>
                    </div>
                  </li>

                  {index < steps.length - 1 ? (
                    <li
                      aria-hidden="true"
                      className={[
                        'mt-3 h-px min-w-12 flex-1',
                        index < safeCurrentStep
                          ? 'bg-[rgb(var(--color-success))]'
                          : 'bg-[rgb(var(--color-border))]',
                      ].join(' ')}
                    />
                  ) : null}
                </React.Fragment>
              );
            })}
          </ol>
        </nav>
      ) : null}

      {progressState ? (
        <div className="shrink-0 border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-6 py-2">
          <div className="flex items-center gap-3">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[rgb(var(--color-border))]">
              <div
                className={[
                  'h-full rounded-full transition-[width,background-color] duration-300',
                  progressState.isComplete
                    ? 'bg-[rgb(var(--color-success))]'
                    : 'bg-[rgb(var(--color-primary))]',
                ].join(' ')}
                style={{ width: `${progressState.percentage}%` }}
              />
            </div>

            <span
              className={[
                'shrink-0 text-right text-tok-xs',
                progressState.isComplete
                  ? 'text-[rgb(var(--color-success))]'
                  : 'text-[rgb(var(--color-text-muted))]',
              ].join(' ')}
            >
              {progressState.configured} of {progressState.total}
              {progressState.label ? ` ${progressState.label}` : ''}
            </span>
          </div>
        </div>
      ) : null}

      <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>

      <footer className="grid shrink-0 grid-cols-3 items-center border-t border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-6 py-3">
        <div className="flex justify-start">
          {safeCurrentStep > 0 ? (
            <button
              type="button"
              onClick={onBack}
              disabled={backDisabled}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] transition-colors hover:bg-[rgb(var(--color-surface-muted))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-focus-ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(var(--color-surface))] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft aria-hidden="true" className="h-4 w-4" />
              Back
            </button>
          ) : null}
        </div>

        <div className="flex justify-center">
          {onSaveDraft ? (
            <button
              type="button"
              onClick={onSaveDraft}
              className="rounded-lg px-3 py-2 text-tok-sm text-[rgb(var(--color-text-muted))] transition-colors hover:bg-[rgb(var(--color-surface-muted))] hover:text-[rgb(var(--color-text))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-focus-ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(var(--color-surface))]"
            >
              Save Draft
            </button>
          ) : null}
        </div>

        <div className="flex justify-end">
          {isLastStep ? (
            <button
              type="button"
              onClick={onSubmit || undefined}
              disabled={submitDisabled || !onSubmit}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-medium text-white transition-colors hover:bg-[rgb(var(--color-primary-hover))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-focus-ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(var(--color-surface))] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitLabel}
            </button>
          ) : (
            <button
              type="button"
              onClick={onNext}
              disabled={nextDisabled}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-medium text-white transition-colors hover:bg-[rgb(var(--color-primary-hover))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-focus-ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(var(--color-surface))] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {nextLabel}
              <ChevronRight aria-hidden="true" className="h-4 w-4" />
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}
